import { useMobileContext } from '@hooks/use-mobile-context.ts'
import { type MobileState } from '@store/mobile-reducer.ts'
//import { useMobileUtils } from '@ui/pages/mobile-upload/mobile-upload.utils.ts'
import React, { useEffect, useState } from 'react'
import { useApi } from '@hooks/use-api-requests.hook.ts'
import { useOCR } from '@hooks/use-ocr.hooks.ts'
import { useValidateSession } from '../../hooks/use-validate-session.hook'
import { getError, isEXTAllowed, isOverWeight } from '@utils/validators'
import { scrollToTop } from '@utils/iframe'
import { useMobileUtils } from '../../mobile-upload.utils'

type SaveDocument = {
	Response: {
		StatusCode: number
	}
}

export const EXT_ALLOWED = [
	'.jpg',
	'.jpeg',
	'.png',
	'.tiff',
	'.heic',
	'.heif',
	'.pdf',
]

function useUpload(
	id: keyof MobileState['steps'],
	enableOCR = true,
	areFieldsDynamic = false
) {
	const { state, dispatch } = useMobileContext()
	const {
		fetchOCR,
		completeStatus,
		error: OCRError,
		setError: setOCRError,
		isCIE,
	} = useOCR(id)

	const [files, updateFiles] = React.useState<FormData | null>(null)
	const [showLoader, setLoader] = useState(false)

	const { moveTo } = useMobileUtils()
	const { validateSession, validSession, isFetchingSession } =
		useValidateSession()

	const [uploadedFiles, uploadFiles] = useApi<SaveDocument>({
		request: 'DbOperations/SaveDocuments',
		sleeper: true,
		file: true,
		method: 'POST',
		shouldCache: false,
	})

	const createPayload = (documentSide: string, file: File | null) => ({
		step: id,
		file,
		documentSide,
	})

	const setMobileUpload = (side: string, photo: File) => {
		setOCRError(getError('NONE'))

		if (!isOverWeight(photo) && isEXTAllowed(photo)) {
			dispatch({
				type: 'SET_PHOTO',
				payload: createPayload(side, photo),
			})

			if (areFieldsDynamic) {
				dispatch({
					type: 'ADD_DOCUMENT_PAGE',
					payload: id,
				})
			}
		}
	}

	const deleteMobileUpload = (side: string) => {
		dispatch({
			type: 'SET_PHOTO',
			payload: createPayload(side, null),
		})

		if (areFieldsDynamic) {
			dispatch({
				type: 'DELETE_LATEST_DOCUMENT_PAGE',
				payload: id,
			})
		}
	}

	const uploadPhotos = () => {
		const formData = new FormData()

		Object.keys(state.steps[id].documents).forEach((key) => {
			const current = state.steps[id].documents[key].content || ''
			if (current !== '') {
				formData.set(key, current)
			}
		})

		formData.set('DocumentType', id)
		formData.set('isCIE', isCIE.toString())

		setLoader(true)
		updateFiles(formData)

		if (state.isSessionActive) {
			validateSession({})
		} else if (enableOCR) {
			fetchOCR({
				body: formData,
			})
		} else {
			uploadFiles({
				body: formData,
			})
		}
	}

	useEffect(() => {
		if (files) {
			files.set('isCIE', isCIE.toString())
		}
	}, [isCIE])

	useEffect(() => {
		if (!isFetchingSession && validSession) {
			if (enableOCR && files) {
				fetchOCR({
					body: files,
				})
			} else if (files) {
				uploadFiles({ body: files })
			}
		}
	}, [validSession, isFetchingSession])

	useEffect(() => {
		if (completeStatus && OCRError.type === 'NONE' && files) {
			dispatch({
				type: 'SET_UPLOAD_VALID',
				payload: {
					step: id,
					valid: true,
				},
			})
			uploadFiles({ body: files })
		} else if (OCRError.type !== 'NONE') {
			setLoader(false)
			dispatch({
				type: 'SET_UPLOAD_VALID',
				payload: {
					step: id,
					valid: false,
				},
			})
		}
	}, [completeStatus, OCRError])

	useEffect(() => {
		if (uploadedFiles && uploadedFiles.Response === null) {
			if (isCIE) {
				dispatch({
					type: 'SET_UPLOAD_VALID',
					payload: {
						step: 'CF',
						valid: true,
					},
				})
				dispatch({
					type: 'SET_UPLOAD_STATE',
					payload: {
						step: 'CF',
						done: true,
					},
				})
			}

			setLoader(false)
			dispatch({
				type: 'SET_UPLOAD_STATE',
				payload: {
					step: id,
					done: true,
				},
			})

			scrollToTop()
			moveTo('SELEZIONE')
		} else if (uploadedFiles) {
			setLoader(false)
			setOCRError(getError('GENERIC'))
		}
	}, [uploadedFiles])

	useEffect(() => {
		const documents = Object.keys(state.steps[id].documents)
		const validityClause = (key: string) =>
			state.steps[id].documents[key].content !== null &&
			OCRError.type === 'NONE'

		const everyValid = documents.every(validityClause)
		const someValid = documents.some(validityClause)

		dispatch({
			type: 'SET_UPLOAD_VALID',
			payload: {
				step: id,
				/* this distinction is made due to expectation.
				 * we should expect every photo to be valid when fields are not dynamic
				 * and each of them is required to move forward.
				 * if fields are dynamic we should one expect at least one element to be valid
				 */
				valid: areFieldsDynamic ? someValid : everyValid,
			},
		})
	}, [state.steps[id].documents])

	return {
		actions: {
			setMobileUpload,
			deleteMobileUpload,
			uploadPhotos,
			moveTo,
		},
		data: {
			isValid: state.steps[id].valid,
			boxes: state.steps[id].documents,
			showLoader,
			OCRError,
		},
	}
}

export { useUpload }
